import React from 'react';
import { Link } from 'react-router-dom';

// The empty screen is an invitation to act. Two steps, in the order they should
// happen — the numbering earns its place because this genuinely is a sequence,
// not decoration.
const TEAL = '#00C6AE';

export function isAtsConnected(integrations) {
  return (integrations || []).some((i) => i && i.is_enabled && i.has_credentials);
}

function StepCard({ n, title, children, done, action }) {
  return (
    <li className="flex gap-4">
      <span
        className={`flex-none w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold tabular-nums ${
          done ? 'text-white' : 'text-gray-500 bg-gray-100'
        }`}
        style={done ? { background: TEAL } : undefined}
        aria-hidden="true"
      >
        {done ? '✓' : n}
      </span>
      <div className="flex-1 min-w-0 pb-6">
        <h3 className="text-sm font-semibold text-gray-900">
          <span className="sr-only">Step {n}: </span>
          {title}
        </h3>
        <div className="text-sm text-gray-500 mt-1">{children}</div>
        <div className="mt-3">{action}</div>
      </div>
    </li>
  );
}

export default function ImportOnboarding({ integrations, onStartImport, starting, isAdmin, disabled }) {
  const connected = isAtsConnected(integrations);

  if (!isAdmin) {
    return (
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
        <h2 className="text-lg font-semibold text-gray-900">No data yet</h2>
        <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
          Quality metrics appear once your organisation&apos;s first import has run. An admin needs to start it.
        </p>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
      <div className="max-w-xl">
        <h2 className="text-xl font-semibold text-gray-900">Bring your data in</h2>
        <p className="text-sm text-gray-500 mt-1.5">
          SignalCue reads your companies, people, jobs and their whole hiring history from your ATS,
          cleans them, then measures how complete they are. Two steps and you&apos;re running.
        </p>
      </div>

      <ol className="mt-7 max-w-xl">
        <StepCard
          n="1"
          title="Connect your ATS"
          done={connected}
          action={
            <Link
              to="/admin?tab=integrations"
              className="inline-flex items-center px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ '--tw-ring-color': TEAL }}
            >
              {connected ? 'Manage integration' : 'Set up integration'}
            </Link>
          }
        >
          {connected
            ? 'Your ATS is connected. Credentials are stored and ready.'
            : 'Point SignalCue at the system your recruiters already work in.'}
        </StepCard>

        <StepCard
          n="2"
          title="Run your first import"
          action={
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onStartImport}
                disabled={starting || disabled}
                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ background: TEAL, '--tw-ring-color': TEAL }}
              >
                {starting ? 'Starting…' : 'Start import'}
              </button>
              <span className="text-xs text-gray-400">Takes a few minutes. You can watch it run.</span>
            </div>
          }
        >
          Read every company, person and job, then clean them into canonical records.
          <p className="text-xs text-gray-400 mt-1.5">
            Imports currently load a sample ATS dataset. Pulling records from a connected ATS is coming.
          </p>
        </StepCard>
      </ol>
    </section>
  );
}
